import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import { trackEvent } from "../analytics";
import { ErrorBox, Loading } from "../components/Loading";
import { Filters, emptyFilters, type FilterValues } from "../components/Filters";

const PAGE_SIZE = 50;
const FILTER_FIELDS: Array<keyof FilterValues> = [
  "playerId",
  "eventId",
  "deckId",
  "from",
  "to",
];

const FILTER_FIELD_LABEL: Record<keyof FilterValues, string> = {
  playerId: "player",
  eventId: "event",
  deckId: "deck",
  from: "from",
  to: "to",
};

function hasValue(value: FilterValues[keyof FilterValues]): boolean {
  return value !== "" && value !== null && value !== undefined;
}

export function MatchesPage() {
  const [filters, setFilters] = useState<FilterValues>(emptyFilters);
  const [page, setPage] = useState(1);
  const filtersRef = useRef(filters);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingChangesRef = useRef<Set<keyof FilterValues>>(new Set());

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const playersQuery = useQuery({ queryKey: ["players"], queryFn: api.players });
  const decksQuery = useQuery({ queryKey: ["decks"], queryFn: api.decks });
  const eventsQuery = useQuery({ queryKey: ["events"], queryFn: () => api.events() });

  const matchesQuery = useQuery({
    queryKey: ["matches", filters, page],
    queryFn: () =>
      api.matches({
        playerId: filters.playerId === "" ? undefined : filters.playerId,
        eventId: filters.eventId === "" ? undefined : filters.eventId,
        deckId: filters.deckId === "" ? undefined : filters.deckId,
        from: filters.from || undefined,
        to: filters.to || undefined,
        page,
        pageSize: PAGE_SIZE,
      }),
  });

  function handleFilterChange(next: FilterValues) {
    const previous = filtersRef.current;
    filtersRef.current = next;
    setFilters(next);
    setPage(1);

    const cleared =
      FILTER_FIELDS.every((field) => !hasValue(next[field])) &&
      FILTER_FIELDS.some((field) => hasValue(previous[field]));

    if (cleared) {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      pendingChangesRef.current.clear();
      trackEvent("matches_filter_clear", {});
      return;
    }

    for (const field of FILTER_FIELDS) {
      if (next[field] !== previous[field]) {
        pendingChangesRef.current.add(field);
      }
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const current = filtersRef.current;
      const fields = Array.from(pendingChangesRef.current);
      pendingChangesRef.current.clear();
      debounceRef.current = null;
      for (const field of fields) {
        trackEvent("matches_filter_change", {
          field: FILTER_FIELD_LABEL[field],
          has_value: hasValue(current[field]),
        });
      }
    }, 600);
  }

  const totalPages = matchesQuery.data
    ? Math.max(1, Math.ceil(matchesQuery.data.total / matchesQuery.data.pageSize))
    : 1;

  function handlePaginate(direction: "next" | "prev") {
    setPage((p) => {
      const nextPage =
        direction === "next"
          ? Math.min(totalPages, p + 1)
          : Math.max(1, p - 1);
      if (nextPage !== p) {
        trackEvent("matches_paginate", { page: nextPage, direction });
      }
      return nextPage;
    });
  }

  return (
    <div className="space-y-6">
      <h1 className="font-display text-[40px] leading-none font-bold tracking-tight text-ink">
        Partidas
      </h1>

      <Filters
        values={filters}
        onChange={handleFilterChange}
        players={playersQuery.data ?? []}
        decks={decksQuery.data ?? []}
        events={eventsQuery.data ?? []}
      />

      {matchesQuery.isLoading && <Loading />}
      {matchesQuery.error && <ErrorBox error={matchesQuery.error} />}

      {matchesQuery.data && (
        <>
          <div className="text-sm text-[#8a8170]">
            {matchesQuery.data.total} partida(s) encontradas
          </div>
          <div className="rounded-2xl border border-line bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-brand-50 text-brand-900">
                <tr className="text-[11px] uppercase tracking-wide">
                  <th className="text-left px-4 py-3 font-bold">Data</th>
                  <th className="text-left px-4 py-3 font-bold">Evento</th>
                  <th className="text-left px-4 py-3 w-12 font-bold">Rd</th>
                  <th className="text-left px-4 py-3 font-bold">Player 1</th>
                  <th className="text-left px-4 py-3 font-bold">Deck 1</th>
                  <th className="text-center px-4 py-3 w-24 font-bold">Placar</th>
                  <th className="text-left px-4 py-3 font-bold">Deck 2</th>
                  <th className="text-left px-4 py-3 font-bold">Player 2</th>
                </tr>
              </thead>
              <tbody>
                {matchesQuery.data.matches.map((m) => (
                  <tr key={m.id} className="border-t border-[#f3efe6]">
                    <td className="px-4 py-2.5 text-[#9a9182] tabular-nums">
                      {m.eventDate}
                    </td>
                    <td className="px-4 py-2.5">
                      <Link
                        to={`/events/${m.eventId}`}
                        className="text-brand-700 hover:underline font-medium"
                      >
                        {m.eventName}
                      </Link>
                    </td>
                    <td className="px-4 py-2.5 tabular-nums text-[#9a9182]">
                      {m.round ?? "—"}
                    </td>
                    <td
                      className={[
                        "px-4 py-2.5 text-ink",
                        m.winnerId === m.player1Id ? "font-bold" : "font-medium",
                      ].join(" ")}
                    >
                      {m.player1Name}
                    </td>
                    <td className="px-4 py-2.5 text-[#6b6356]">
                      {m.player1DeckName ? (
                        m.player1HasDecklist &&
                        m.player1EventResultId !== null ? (
                          <Link
                            to={`/events/${m.eventId}/decks/${m.player1EventResultId}`}
                            className="text-brand-700 hover:underline decoration-dotted underline-offset-2"
                          >
                            {m.player1DeckName}
                          </Link>
                        ) : (
                          m.player1DeckName
                        )
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-center tabular-nums font-display font-bold text-ink">
                      {m.player1Wins}–{m.player2Wins}
                      {m.draws > 0 ? `–${m.draws}` : ""}
                    </td>
                    <td className="px-4 py-2.5 text-[#6b6356]">
                      {m.player2DeckName ? (
                        m.player2HasDecklist &&
                        m.player2EventResultId !== null ? (
                          <Link
                            to={`/events/${m.eventId}/decks/${m.player2EventResultId}`}
                            className="text-brand-700 hover:underline decoration-dotted underline-offset-2"
                          >
                            {m.player2DeckName}
                          </Link>
                        ) : (
                          m.player2DeckName
                        )
                      ) : (
                        "—"
                      )}
                    </td>
                    <td
                      className={[
                        "px-4 py-2.5 text-ink",
                        m.winnerId !== null && m.winnerId === m.player2Id
                          ? "font-bold"
                          : "font-medium",
                      ].join(" ")}
                    >
                      {m.player2Name ?? (
                        <span className="italic text-slate-400">bye</span>
                      )}
                    </td>
                  </tr>
                ))}
                {matchesQuery.data.matches.length === 0 && (
                  <tr>
                    <td
                      colSpan={8}
                      className="text-center text-slate-500 py-8"
                    >
                      Nenhuma partida encontrada com esses filtros.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 text-sm">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => handlePaginate("prev")}
                className="px-4 py-1.5 rounded-lg border border-line bg-white font-medium disabled:opacity-40 hover:bg-[#f3efe6] disabled:hover:bg-white"
              >
                Anterior
              </button>
              <span className="text-[#8a8170]">
                Página {page} de {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => handlePaginate("next")}
                className="px-4 py-1.5 rounded-lg border border-line bg-white font-medium disabled:opacity-40 hover:bg-[#f3efe6] disabled:hover:bg-white"
              >
                Próxima
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
